import React from 'react'
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@common/components'
import { Flag, Steppper } from './onboarding-modal.styles'
import 'flag-icons/css/flag-icons.min.css'
import { useLanguages } from '@features/languages/hooks'
import { Language } from '@types'
import { useModulesSubmitModuleResultMutation } from '@app/services/auto-generated/modules'
import { useAppDispatch } from '@common/hooks'
import { modulesApi } from '@app/services'
import { useToast } from '@features/toast/hooks'
interface FormState {
  englishLevel: string
  nativeLanguage: string
  otherLanguage: string
  referralSource: string
  specificReferralSource?: string
}

const OnboardingModal = () => {
  const [step, setStep] = React.useState(1)
  const [formState, setFormState] = React.useState<FormState>({
    englishLevel: '',
    nativeLanguage: '',
    otherLanguage: '',
    referralSource: '',
    specificReferralSource: '',
  })
  const [submitModule] = useModulesSubmitModuleResultMutation()
  const dispatch = useAppDispatch()
  const toast = useToast()
  const handleSubmit = async () => {
    const patchResult = dispatch(
      modulesApi.util.updateQueryData('modulesGetUserModules', undefined, (draft) => {
        draft.required = draft.required.filter((m) => m !== 'CollectInformationModule')
        draft.submitted.push('CollectInformationModule')
      }),
    )
    try {
      await submitModule({
        moduleResult: {
          id: 'CollectInformationModule',
          data: {
            english_level: formState.englishLevel,
            native_language: formState.otherLanguage || formState.nativeLanguage,
            referral_source: formState.referralSource,
            specific_referral_source: formState.specificReferralSource,
          },
        },
      }).unwrap()
      dispatch(modulesApi.util.invalidateTags(['Modules']))
      toast('Welcome to SpeakDuo!', 'success')
    } catch {
      patchResult.undo()
      toast('Something went wrong', 'error')
    }
  }

  const isLastStep = step === 3

  return (
    <Modal
      open={true}
      onClose={() => {}}
      showCloseBtn={false}
      paperProps={{ sx: { p: 5, width: '500px', maxWidth: '100%' } }}
    >
      <Box>
        <Stack spacing={2}>
          <Grid container spacing={1} sx={{ width: '100%' }}>
            {Array(3)
              .fill(null)
              ?.map((_, index) => (
                <Grid key={index} size={4}>
                  <Steppper
                    sx={{
                      background: (theme) => (step > index ? '#48b7f7' : theme.palette.divider),
                    }}
                  />
                </Grid>
              ))}
          </Grid>
          <StepContent step={step} formState={formState} setFormState={setFormState} />
        </Stack>
        <Stack direction={'row'} justifyContent={'space-between'} mt={4} sx={{ width: '100%' }}>
          {step > 1 && (
            <Button variant='contained' onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          <Button
            variant='contained'
            onClick={() => (isLastStep ? handleSubmit() : setStep(step + 1))}
            sx={{ ml: 'auto' }}
          >
            {isLastStep ? 'Submit' : 'Next'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  )
}

const StepContent = ({
  step,
  formState,
  setFormState,
}: {
  step: number
  formState: FormState
  setFormState: React.Dispatch<React.SetStateAction<FormState>>
}) => {
  const { languages } = useLanguages()

  if (step === 1) {
    return (
      <Stack>
        <Typography variant='body2' fontWeight={'bold'} mb={2}>
          What is your current English level?
        </Typography>
        <FormControl>
          <RadioGroup
            sx={{ gap: 4 }}
            value={formState.englishLevel}
            onChange={(e) => setFormState((prev) => ({ ...prev, englishLevel: e.target.value }))}
          >
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
              }}
              value='beginner'
              control={<Radio size='small' sx={{ top: -8 }} />}
              label={
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%' }}>
                  <Typography variant='body2' fontWeight={'bold'} sx={{ width: '90px' }}>
                    Beginner
                  </Typography>
                  <Typography variant='body2' sx={{ flex: 1 }}>
                    You can understand only some expressions. You can say a few simple sentences.
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
              }}
              value='intermediate'
              control={<Radio size='small' sx={{ top: -8 }} />}
              label={
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%' }}>
                  <Typography variant='body2' fontWeight={'bold'} sx={{ width: '90px' }}>
                    Intermediate
                  </Typography>
                  <Typography variant='body2' sx={{ flex: 1 }}>
                    You can hold basic conversations, and understand some idiomatic expressions.
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
              }}
              value='advanced'
              control={<Radio size='small' sx={{ top: -8 }} />}
              label={
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%' }}>
                  <Typography variant='body2' fontWeight={'bold'} sx={{ width: '90px' }}>
                    Advanced
                  </Typography>
                  <Typography variant='body2' sx={{ flex: 1 }}>
                    You speak English regularly and you can understand complex dialogue.
                  </Typography>
                </Stack>
              }
            />
          </RadioGroup>
        </FormControl>
      </Stack>
    )
  }
  if (step === 2) {
    return (
      <Stack>
        <Typography variant='body2' fontWeight={'bold'} mb={2}>
          What is your native language?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {langs.map((language) => (
            <Tooltip title={language.name}>
              <Flag
                key={language.code}
                isSelected={formState.nativeLanguage === language.name}
                onClick={() =>
                  setFormState((prev) => ({
                    ...prev,
                    nativeLanguage: language.name,
                    otherLanguage: '',
                  }))
                }
              >
                <span className={`fi fi-${language.code === 'en' ? 'gb' : language.code}`}></span>
              </Flag>
            </Tooltip>
          ))}
        </Box>
        <Typography variant='body2' fontWeight={'bold'} sx={{ my: 2 }}>
          Or select from the list below:
        </Typography>
        <Autocomplete
          options={languages}
          value={languages.find((lang) => lang.name === formState.otherLanguage) || null}
          getOptionLabel={(option: Language) => option.name ?? ''}
          onChange={(_: React.SyntheticEvent, newValue: Language | null) => {
            setFormState((prev) => ({
              ...prev,
              otherLanguage: newValue?.name || '',
              nativeLanguage: '',
            }))
          }}
          renderOption={(
            props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
            option: Language,
          ) => {
            const { key, ...otherProps } = props
            return (
              <Box component='li' key={key} {...otherProps}>
                <Typography sx={{ flex: 1 }}>{option.name}</Typography>
              </Box>
            )
          }}
          renderInput={(params: any) => {
            return <TextField {...params} placeholder='Other languages' size='small' />
          }}
          size='small'
        />
      </Stack>
    )
  }
  if (step === 3) {
    return (
      <Stack>
        <Typography variant='body2' fontWeight={'bold'} mb={2}>
          Where did you first hear about SpeakDuo?
        </Typography>
        <FormControl>
          <RadioGroup
            sx={{ gap: 2 }}
            value={formState.referralSource}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                referralSource: e.target.value,
                specificReferralSource: '',
              }))
            }
          >
            <Stack>
              <FormControlLabel value='meetup' control={<Radio size='small' />} label='Meetup' />
              {formState.referralSource === 'meetup' && (
                <TextField
                  placeholder='Please specify the group name if you know'
                  size='small'
                  value={formState.specificReferralSource}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, specificReferralSource: e.target.value }))
                  }
                />
              )}
            </Stack>

            <Stack>
              <FormControlLabel value='google' control={<Radio size='small' />} label='Google' />
              {formState.referralSource === 'google' && (
                <TextField
                  placeholder='Please specify what you searched'
                  size='small'
                  value={formState.specificReferralSource}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, specificReferralSource: e.target.value }))
                  }
                />
              )}
            </Stack>

            <Stack>
              <FormControlLabel
                value='social Network'
                control={<Radio size='small' />}
                label='TikTok / Instagram / Facebook'
              />
              {formState.referralSource === 'social Network' && (
                <TextField
                  placeholder='Please specify the channel name if you know'
                  size='small'
                  value={formState.specificReferralSource}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, specificReferralSource: e.target.value }))
                  }
                />
              )}
            </Stack>

            <Stack>
              <FormControlLabel
                value='referred By Friend'
                control={<Radio size='small' />}
                label='Referred by a friend'
              />
              {formState.referralSource === 'referred By Friend' && (
                <TextField
                  placeholder='Please specify'
                  size='small'
                  value={formState.specificReferralSource}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, specificReferralSource: e.target.value }))
                  }
                />
              )}
            </Stack>

            <Stack>
              <FormControlLabel value='other' control={<Radio size='small' />} label='Other' />
              {formState.referralSource === 'other' && (
                <TextField
                  placeholder='Please specify'
                  size='small'
                  value={formState.specificReferralSource}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, specificReferralSource: e.target.value }))
                  }
                />
              )}
            </Stack>
          </RadioGroup>
        </FormControl>
      </Stack>
    )
  }
  return null
}

const langs = [
  {
    name: 'English',
    code: 'en',
  },
  {
    name: 'French',
    code: 'fr',
  },
  {
    name: 'German',
    code: 'de',
  },
  {
    name: 'Italian',
    code: 'it',
  },
  {
    name: 'Spanish',
    code: 'es',
  },
  {
    name: 'Portuguese',
    code: 'pt',
  },
  {
    name: 'Russian',
    code: 'ru',
  },
  {
    name: 'Arabic',
    code: 'sa',
  },
  {
    name: 'Japanese',
    code: 'jp',
  },
  {
    name: 'Korean',
    code: 'kr',
  },
]

export default OnboardingModal
